import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toUnit, harvestYard, harvestHiveIds, harvestRevenue, buildApiaryYield } from './harvestStats';

export const AMBER = [217, 119, 6];
export const AMBER_DARK = [146, 64, 14];
const HONEY = [252, 211, 77];
export const CREAM = [255, 251, 235];
export const INK = [41, 37, 36];
export const MUTED = [120, 113, 108];
const PALETTE = [[217, 119, 6], [234, 179, 8], [13, 148, 136], [124, 58, 237], [219, 39, 119], [37, 99, 235], [101, 163, 13], [120, 113, 108]];
const W = 210, M = 14;
const money = (v) => `$${(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
let UNIT = 'kg';
const kg = (v) => `${(Math.round((v || 0) * 10) / 10).toLocaleString('en-AU')} ${UNIT}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');

export const hexagon = (doc, cx, cy, r, style) => {
  const pts = Array.from({ length: 6 }, (_, i) => [cx + r * Math.cos((Math.PI / 3) * i + Math.PI / 6), cy + r * Math.sin((Math.PI / 3) * i + Math.PI / 6)]);
  doc.lines(pts.slice(1).map((p, i) => [p[0] - pts[i][0], p[1] - pts[i][1]]), pts[0][0], pts[0][1], [1, 1], style, true);
};

export const sectionTitle = (doc, title, y, sub) => {
  doc.setFillColor(...AMBER);
  hexagon(doc, M + 2.5, y - 1.6, 2.6, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...INK);
  doc.text(title, M + 8, y);
  if (sub) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(sub, W - M, y, { align: 'right' });
  }
  return y + 6;
};

export const tile = (doc, x, y, w, h, value, label, accent = AMBER) => {
  doc.setFillColor(...CREAM);
  doc.setDrawColor(253, 230, 138);
  doc.roundedRect(x, y, w, h, 3, 3, 'FD');
  doc.setFillColor(...accent);
  doc.roundedRect(x, y, 2.2, h, 1, 1, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(h > 20 ? 18 : 13);
  doc.setTextColor(...accent);
  doc.text(String(value), x + 6, y + h / 2 + (h > 20 ? 1 : 0.5));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text(label.toUpperCase(), x + 6, y + h - 4);
};

const hBars = (doc, rows, y, { labelW = 42, valueFmt = kg, highlightFirst = true, color = HONEY } = {}) => {
  const max = Math.max(...rows.map(r => r.value), 0.0001);
  const barW = W - 2 * M - labelW - (rows.some(r => r.extra) ? 50 : 26);
  rows.forEach((r, i) => {
    doc.setFont('helvetica', i === 0 && highlightFirst ? 'bold' : 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...INK);
    doc.text(doc.splitTextToSize(r.label, labelW - 2)[0], M, y + 4);
    doc.setFillColor(245, 245, 244);
    doc.roundedRect(M + labelW, y, barW, 5.5, 2, 2, 'F');
    doc.setFillColor(...(i === 0 && highlightFirst ? AMBER : color));
    doc.roundedRect(M + labelW, y, Math.max(2, (r.value / max) * barW), 5.5, 2, 2, 'F');
    doc.setTextColor(...(i === 0 && highlightFirst ? AMBER_DARK : MUTED));
    doc.text(valueFmt(r.value) + (r.extra ? `  ${r.extra}` : ''), M + labelW + barW + 2, y + 4);
    y += 8;
  });
  return y;
};

const footer = (doc, appName) => {
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setDrawColor(253, 230, 138);
    doc.setLineWidth(0.4);
    doc.line(M, 285, W - M, 285);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(`${appName} · Harvest Report`, M, 290);
    doc.text(`Page ${i} of ${pages}`, W - M, 290, { align: 'right' });
  }
};

export const buildHarvestReport = ({ unit = 'kg', harvests, hives = [], apiaryLabel, seasonLabel, ownerName, businessName, logoDataUrl, appName = 'Thrive Beekeeping', yieldHarvests }) => {
  UNIT = unit;
  const toKg = (h) => toUnit(h, unit);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const totalKg = harvests.reduce((s, h) => s + toKg(h), 0);
  const hiveSet = new Set();
  harvests.forEach(h => harvestHiveIds(h).forEach(id => hiveSet.add(id)));
  const hivesHarvested = hiveSet.size;
  const frames = harvests.reduce((s, h) => s + (parseInt(h.frames_harvested) || 0), 0);
  const revenue = harvests.reduce((s, h) => s + harvestRevenue(h), 0);
  const soldRevenue = harvests.filter(h => h.is_sold).reduce((s, h) => s + harvestRevenue(h), 0);
  const moist = harvests.map(h => parseFloat(h.moisture_percent)).filter(v => v > 0);
  const avgMoist = moist.length ? moist.reduce((a, b) => a + b, 0) / moist.length : null;
  const yieldStats = buildApiaryYield(yieldHarvests || harvests, hives, unit);

  const byType = {};
  harvests.forEach(h => {
    const t = h.honey_type || 'Unspecified';
    const g = (byType[t] = byType[t] || { type: t, kg: 0, revenue: 0, soldKg: 0, pricedKg: 0, count: 0 });
    g.kg += toKg(h); g.count += 1; g.revenue += harvestRevenue(h);
    if (h.is_sold) g.soldKg += toKg(h);
    if (parseFloat(h.sale_price) > 0) g.pricedKg += toKg(h);
  });
  const types = Object.values(byType).sort((a, b) => b.kg - a.kg);

  const byMonth = {};
  harvests.forEach(h => {
    if (!h.date) return;
    const d = new Date(h.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    byMonth[key] = (byMonth[key] || 0) + toKg(h);
  });
  const months = Object.keys(byMonth).sort();

  const byHive = {};
  harvests.forEach(h => {
    const ids = harvestHiveIds(h);
    const nums = h.hive_numbers?.length ? h.hive_numbers : [h.hive_number].filter(Boolean);
    const share = toKg(h) / Math.max(1, ids.length || nums.length || 1);
    (ids.length ? ids : nums.map(n => `#${n}`)).forEach((id, i) => {
      const hive = hives.find(x => x.id === id);
      const label = hive ? `Hive ${hive.hive_number}${hive.yard ? ` · ${hive.yard}` : ''}` : `Hive ${nums[i] ?? id}`;
      byHive[label] = (byHive[label] || 0) + share;
    });
  });
  const topHives = Object.entries(byHive).sort((a, b) => b[1] - a[1]).slice(0, 5);

  // ---------- PAGE 1: cover ----------
  doc.setFillColor(...AMBER);
  doc.rect(0, 0, W, 62, 'F');
  doc.setDrawColor(251, 191, 36);
  doc.setLineWidth(0.5);
  for (let r = 0; r < 4; r++) for (let c = 0; c < 6; c++) hexagon(doc, 128 + c * 15 + (r % 2) * 7.5, 4 + r * 13, 7.5, 'S');
  doc.setFillColor(...AMBER);
  doc.rect(0, 52, W, 10, 'F');
  let logoW = 0;
  if (logoDataUrl) {
    try {
      const { width, height } = doc.getImageProperties(logoDataUrl);
      const ratio = width / height;
      const lw = ratio >= 1 ? 30 : 18 * ratio, lh = ratio >= 1 ? 30 / ratio : 18;
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(M, 12, lw + 4, Math.max(lh + 4, 12), 3, 3, 'F');
      doc.addImage(logoDataUrl, M + 2, 12 + (Math.max(lh + 4, 12) - lh) / 2, lw, lh);
      logoW = lw + 4;
    } catch { /* ignore bad logo */ }
  }
  const tx = logoW ? M + logoW + 5 : M;
  doc.setTextColor(255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text((businessName || appName).toUpperCase(), tx, 17);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.text('Harvest Report', tx, 29);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.text(`${ownerName ? `Prepared for ${ownerName}  ·  ` : ''}Generated ${fmtDate(new Date())}`, tx, 36);
  const chips = [`Apiary: ${apiaryLabel}`, `Season: ${seasonLabel}`];
  let cx = M;
  doc.setFontSize(9);
  chips.forEach(c => {
    const cw = doc.getTextWidth(c) + 10;
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(cx, 44, cw, 8, 4, 4, 'F');
    doc.setTextColor(...AMBER_DARK);
    doc.setFont('helvetica', 'bold');
    doc.text(c, cx + 5, 49.4);
    cx += cw + 4;
  });

  let y = 72;
  const tw = (W - 2 * M - 9) / 4;
  tile(doc, M, y, tw, 26, kg(totalKg), 'Total harvested');
  tile(doc, M + (tw + 3), y, tw, 26, hivesHarvested ? kg(totalKg / hivesHarvested) : '-', 'Per hive harvested', [13, 148, 136]);
  tile(doc, M + 2 * (tw + 3), y, tw, 26, frames, 'Frames extracted', [202, 138, 4]);
  tile(doc, M + 3 * (tw + 3), y, tw, 26, money(revenue), 'Revenue value', [22, 163, 74]);
  y += 30;
  tile(doc, M, y, tw, 16, harvests.length, 'Harvests', MUTED);
  tile(doc, M + (tw + 3), y, tw, 16, hivesHarvested, 'Hives harvested', MUTED);
  tile(doc, M + 2 * (tw + 3), y, tw, 16, avgMoist ? `${avgMoist.toFixed(1)}%` : '-', 'Avg moisture', MUTED);
  tile(doc, M + 3 * (tw + 3), y, tw, 16, revenue ? `${Math.round((soldRevenue / revenue) * 100)}%` : '-', 'Of value sold', MUTED);
  y += 26;

  // Monthly yield chart
  y = sectionTitle(doc, 'Monthly Yield', y, `${unit} harvested each month`);
  const chartH = 58, chartTop = y + 2, chartW = W - 2 * M;
  const maxM = Math.max(...months.map(m => byMonth[m]), 1);
  doc.setDrawColor(231, 229, 228);
  doc.setLineWidth(0.2);
  for (let g = 0; g <= 4; g++) {
    const gy = chartTop + chartH - (g / 4) * chartH;
    doc.line(M + 10, gy, M + chartW, gy);
    doc.setFontSize(7); doc.setTextColor(...MUTED);
    doc.text(String(Math.round((maxM * g) / 4)), M + 8, gy + 1, { align: 'right' });
  }
  if (months.length === 0) {
    doc.setFontSize(9); doc.text('No dated harvests', W / 2, chartTop + chartH / 2, { align: 'center' });
  }
  const slot = (chartW - 12) / Math.max(months.length, 1);
  const bestMonth = months.reduce((b, m) => (byMonth[m] > (byMonth[b] || 0) ? m : b), months[0]);
  months.forEach((m, i) => {
    const bh = (byMonth[m] / maxM) * chartH;
    const bw = Math.min(16, slot * 0.6);
    const bx = M + 12 + i * slot + (slot - bw) / 2;
    doc.setFillColor(...(m === bestMonth ? AMBER : HONEY));
    doc.roundedRect(bx, chartTop + chartH - bh, bw, bh, 1.5, 1.5, 'F');
    doc.setFontSize(7.5); doc.setTextColor(...INK);
    doc.text(String(Math.round(byMonth[m])), bx + bw / 2, chartTop + chartH - bh - 1.5, { align: 'center' });
    const [yy, mm] = m.split('-');
    doc.setTextColor(...MUTED);
    doc.text(new Date(+yy, +mm - 1, 1).toLocaleDateString('en-AU', { month: 'short' }) + ` ${yy.slice(2)}`, bx + bw / 2, chartTop + chartH + 4.5, { align: 'center' });
  });
  y = chartTop + chartH + 12;

  // Highlights
  y = sectionTitle(doc, 'Season Highlights', y);
  const highlights = [
    yieldStats[0] && yieldStats.length > 1 ? ['Most productive apiary', `${yieldStats[0].apiary}: ${kg(yieldStats[0].perHive)} per hive`] : null,
    types[0] ? ['Top honey type', `${types[0].type}: ${Math.round((types[0].kg / Math.max(totalKg, 0.0001)) * 100)}% of the crop`] : null,
    bestMonth ? ['Best month', `${new Date(bestMonth + '-01').toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}: ${kg(byMonth[bestMonth])}`] : null,
    topHives[0] ? ['Star hive', `${topHives[0][0]}: ${kg(topHives[0][1])}`] : null
  ].filter(Boolean);
  const hw = (W - 2 * M - 3) / 2;
  highlights.forEach(([label, value], i) => {
    const hx = M + (i % 2) * (hw + 3), hy = y + Math.floor(i / 2) * 17;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(253, 230, 138);
    doc.roundedRect(hx, hy, hw, 14, 2.5, 2.5, 'FD');
    doc.setFillColor(...HONEY);
    hexagon(doc, hx + 7, hy + 7, 4, 'F');
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
    doc.text(label.toUpperCase(), hx + 14, hy + 5.5);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(...INK);
    doc.text(doc.splitTextToSize(value, hw - 17)[0], hx + 14, hy + 11);
  });

  // ---------- PAGE 2: apiaries, honey types, hives ----------
  doc.addPage();
  y = 20;
  y = sectionTitle(doc, 'Apiary Yield Comparison', y, `${unit} per hive harvested`);
  y = hBars(doc, yieldStats.map(s => ({ label: s.apiary, value: s.perHive, extra: `(${kg(s.total)}, ${s.hives} hive${s.hives !== 1 ? 's' : ''})` })), y + 1, { highlightFirst: yieldStats.length > 1 });
  if (!yieldStats.length) { doc.setFontSize(9); doc.setTextColor(...MUTED); doc.text('No harvests', M, y + 4); y += 8; }
  y += 6;

  y = sectionTitle(doc, 'Honey Types', y, `share of total ${unit}`);
  let sx = M;
  const shareW = W - 2 * M;
  types.forEach((t, i) => {
    const w = (t.kg / Math.max(totalKg, 0.0001)) * shareW;
    doc.setFillColor(...PALETTE[i % PALETTE.length]);
    doc.rect(sx, y, Math.max(w, 0.5), 9, 'F');
    if (w > 14) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(255);
      doc.text(`${Math.round((t.kg / totalKg) * 100)}%`, sx + w / 2, y + 5.8, { align: 'center' });
    }
    sx += w;
  });
  y += 13;
  autoTable(doc, {
    startY: y,
    theme: 'plain',
    styles: { fontSize: 8.5, cellPadding: 2.2, textColor: INK },
    headStyles: { textColor: MUTED, fontStyle: 'bold', fontSize: 7.5 },
    alternateRowStyles: { fillColor: CREAM },
    margin: { left: M, right: M },
    head: [['', 'HONEY TYPE', unit.toUpperCase(), 'SHARE', 'HARVESTS']],
    body: types.map(t => ['', t.type, kg(t.kg), `${Math.round((t.kg / Math.max(totalKg, 0.0001)) * 100)}%`, t.count]),
    columnStyles: { 0: { cellWidth: 6 } },
    didDrawCell: (d) => {
      if (d.section === 'body' && d.column.index === 0) {
        doc.setFillColor(...PALETTE[d.row.index % PALETTE.length]);
        hexagon(doc, d.cell.x + 3, d.cell.y + d.cell.height / 2, 2, 'F');
      }
    }
  });
  y = doc.lastAutoTable.finalY + 10;

  y = sectionTitle(doc, 'Top 5 Hives', y, `${unit} (shared harvests split evenly)`);
  y = hBars(doc, topHives.map(([label, value]) => ({ label, value })), y + 1, { labelW: 50 });
  if (!topHives.length) { doc.setFontSize(9); doc.setTextColor(...MUTED); doc.text('No hive data', M, y + 4); }

  // ---------- PAGE 3: sales + log ----------
  doc.addPage();
  y = 20;
  y = sectionTitle(doc, 'Sales & Revenue', y, 'value = quantity x sale price');
  const pricedKg = types.reduce((s, t) => s + t.pricedKg, 0);
  const sw = (W - 2 * M - 6) / 3;
  tile(doc, M, y, sw, 22, money(soldRevenue), 'Sold', [22, 163, 74]);
  tile(doc, M + sw + 3, y, sw, 22, money(revenue - soldRevenue), 'In stock value', AMBER);
  tile(doc, M + 2 * (sw + 3), y, sw, 22, pricedKg ? `${money(revenue / pricedKg)}/${unit === 'lbs' ? 'lb' : 'kg'}` : '-', 'Avg price', [13, 148, 136]);
  y += 28;
  if (revenue > 0) {
    const soldW = (soldRevenue / revenue) * (W - 2 * M);
    doc.setFillColor(...AMBER); doc.roundedRect(M, y, W - 2 * M, 6, 3, 3, 'F');
    doc.setFillColor(22, 163, 74); if (soldW > 0) doc.roundedRect(M, y, Math.max(soldW, 6), 6, 3, 3, 'F');
    doc.setFontSize(7.5); doc.setTextColor(...MUTED);
    doc.text(`Sold ${Math.round((soldRevenue / revenue) * 100)}%`, M, y + 10);
    doc.text(`In stock ${100 - Math.round((soldRevenue / revenue) * 100)}%`, W - M, y + 10, { align: 'right' });
    y += 15;
  }
  const tableBase = {
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 2, lineColor: [245, 245, 244], textColor: INK },
    headStyles: { fillColor: AMBER, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: CREAM },
    margin: { left: M, right: M }
  };
  autoTable(doc, {
    ...tableBase, startY: y,
    head: [['Honey Type', unit, `${unit} Sold`, `Avg $/${unit === 'lbs' ? 'lb' : 'kg'}`, 'Value', 'Sold %']],
    body: types.map(t => [t.type, kg(t.kg), kg(t.soldKg), t.pricedKg ? money(t.revenue / t.pricedKg) : '-', money(t.revenue), t.kg ? `${Math.round((t.soldKg / t.kg) * 100)}%` : '-'])
  });
  y = doc.lastAutoTable.finalY + 10;
  y = sectionTitle(doc, 'Harvest Log', y, `${harvests.length} record${harvests.length !== 1 ? 's' : ''}`);
  autoTable(doc, {
    ...tableBase, startY: y,
    head: [['Date', 'Apiary', 'Hives', unit, 'Type', 'Grade', 'Frames', 'Moist.', 'Price', 'Status']],
    body: [...harvests].sort((a, b) => (a.date || '').localeCompare(b.date || '')).map(h => [
      fmtDate(h.date), harvestYard(h, hives),
      (h.hive_numbers?.length ? h.hive_numbers : [h.hive_number].filter(Boolean)).map(n => `#${n}`).join(', ') || '-',
      (Math.round(toKg(h) * 10) / 10).toString(), h.honey_type || '-', h.quality_grade || '-',
      h.frames_harvested || '-', h.moisture_percent ? `${h.moisture_percent}%` : '-',
      h.sale_price ? `${money(parseFloat(h.sale_price))}/${(h.unit || 'kg').replace(/s$/, '')}` : '-',
      { content: h.is_sold ? 'Sold' : 'In stock', styles: { textColor: h.is_sold ? [22, 163, 74] : AMBER_DARK, fontStyle: 'bold' } }
    ]),
    columnStyles: { 3: { halign: 'right' }, 6: { halign: 'center' } }
  });

  footer(doc, businessName || appName);
  return doc;
};

export const loadLogo = async () => {
  try {
    const res = await fetch('/thrive-logo.png');
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = () => resolve(null); r.readAsDataURL(blob); });
  } catch {
    return null;
  }
};

export const downloadHarvestReport = async (opts) => {
  const logoDataUrl = await loadLogo();
  const doc = buildHarvestReport({ ...opts, logoDataUrl });
  const slug = `${opts.apiaryLabel}-${opts.seasonLabel}`.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  doc.save(`harvest-report-${slug}.pdf`);
};
